import { Fragment } from 'react';
import { NavLink } from 'react-router-dom';
import { Menu, Transition } from '@headlessui/react';
import {
  UserCircleIcon,
  Cog6ToothIcon,
  ShieldExclamationIcon,
  LockClosedIcon,
  BellIcon,
  ArrowRightOnRectangleIcon,
  AdjustmentsHorizontalIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '@/hooks/useAuth';
import { useSignOut } from '@/hooks/useSignOut';
import { accountName } from '@/utils/accountName';

// ---------------------------------------------------------------------------
// The top bar's user menu
// ---------------------------------------------------------------------------
//
// 🔴 DSH-027: THIS IS THE MENU AppLayout MOUNTS. The top bar used to carry its
// own inline copy offering only Settings and Sign out, while this component —
// with My Profile and its Preferences / Security / Notifications tabs — was
// mounted nowhere. So /profile could be found only through the G P shortcut or
// a text link inside Settings > Notifications. One menu now, this one.
//
// The language buttons this component used to show are gone: their handler
// was a comment ("Would dispatch language change"), and a control that does
// nothing is worse than no control. Language lives in Settings > Profile.

interface UserMenuProps {
  className?: string;
}

const LINKS = [
  { to: '/profile', label: 'My Profile', icon: UserCircleIcon },
  { to: '/profile?tab=preferences', label: 'Preferences', icon: AdjustmentsHorizontalIcon },
  { to: '/profile?tab=security', label: 'Change Password', icon: LockClosedIcon },
  { to: '/profile?tab=notifications', label: 'Notifications', icon: BellIcon },
  { to: '/settings', label: 'Settings', icon: Cog6ToothIcon },
  // R15: anyone may raise one, whatever their role; not only the desk.
  { to: '/reception/concern', label: 'Private concern', icon: ShieldExclamationIcon },
] as const;

export default function UserMenu({ className = '' }: UserMenuProps) {
  const { user } = useAuth();
  // A16: asks first when unsent work is on the device.
  const { signOut, signOutDialog } = useSignOut();

  const initials = user
    ? `${user.first_name?.[0] ?? ''}${user.last_name?.[0] ?? ''}`.toUpperCase()
    : '??';
  const name = accountName(user);
  const role = user?.role?.replace(/_/g, ' ') ?? 'Employee';

  return (
    <Menu as="div" className={`relative ${className}`}>
      <Menu.Button
        className="flex items-center gap-2 rounded-lg p-1.5 transition-colors hover:bg-surface-muted"
        aria-label="Open user menu"
      >
        {user?.avatar_url ? (
          <img
            src={user.avatar_url}
            alt={name || 'Profile photo'}
            className="h-8 w-8 rounded-full object-cover"
            onError={(e) => {
              // Avatar URL exists but the file is missing (404): swap the
              // broken <img> for the initials fallback in place.
              const img = e.currentTarget as HTMLImageElement;
              const fallback = document.createElement('div');
              fallback.className = 'flex h-8 w-8 items-center justify-center rounded-full bg-brand-700 text-xs font-semibold text-white';
              fallback.textContent = initials;
              img.replaceWith(fallback);
            }}
          />
        ) : (
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-700 text-xs font-semibold text-white">
            {initials}
          </div>
        )}
        <div className="hidden text-left lg:block">
          <p className="text-sm font-medium text-content">{name}</p>
          <p className="text-xs text-content-muted capitalize">{role}</p>
        </div>
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 z-50 mt-2 w-60 origin-top-right rounded-xl border border-line bg-surface py-1 shadow-lg focus:outline-none">
          <div className="border-b border-line-subtle px-4 py-3">
            <p className="text-sm font-medium text-content">{name}</p>
            <p className="text-xs text-content-muted">{user?.email}</p>
          </div>

          <div className="py-1">
            {LINKS.map(({ to, label, icon: Icon }) => (
              <Menu.Item key={to}>
                {({ active }) => (
                  <NavLink
                    to={to}
                    className={`flex items-center gap-3 px-4 py-2 text-sm ${
                      active ? 'bg-surface-subtle text-content' : 'text-content-tertiary'
                    }`}
                  >
                    <Icon className="h-4 w-4" aria-hidden="true" />
                    {label}
                  </NavLink>
                )}
              </Menu.Item>
            ))}
          </div>

          <div className="border-t border-line-subtle py-1">
            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={() => { void signOut(); }}
                  className={`flex w-full items-center gap-3 px-4 py-2 text-left text-sm ${
                    active ? 'bg-surface-subtle text-status-red' : 'text-content-tertiary'
                  }`}
                >
                  <ArrowRightOnRectangleIcon className="h-4 w-4" aria-hidden="true" />
                  Sign out
                </button>
              )}
            </Menu.Item>
          </div>
        </Menu.Items>
      </Transition>
      {signOutDialog}
    </Menu>
  );
}
